import { TEAM_API_PREFIX } from '@ftedn/shared';
import { useMemo } from 'react';
import { getAppCheckToken } from '../services/app-check';

// FTEDN — API client hook
interface AppConfig {
  apiBase: string;
  googleClientId?: string;
}

export class ApiClientError extends Error {
  code?: string;

  constructor(message: string, code?: string) {
    super(message);
    this.name = 'ApiClientError';
    this.code = code;
  }
}

let cachedConfig: AppConfig | null = null;

function getConfig(): AppConfig {
  if (cachedConfig) return cachedConfig;

  const prefix = import.meta.env.VITE_API_PREFIX || TEAM_API_PREFIX;
  const origin = (import.meta.env.VITE_API_ORIGIN || import.meta.env.VITE_API_URL || '').replace(/\/$/, '');
  cachedConfig = {
    apiBase: origin ? `${origin}${prefix}` : prefix,
    googleClientId: import.meta.env.VITE_GOOGLE_CLIENT_ID || '',
  };
  return cachedConfig;
}

export async function getGoogleClientId(): Promise<string> {
  const config = await getConfig();
  return config.googleClientId || '';
}

class ApiClient {
  private async getHeaders(): Promise<Record<string, string>> {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    const storage = typeof localStorage !== 'undefined' && typeof localStorage.getItem === 'function' ? localStorage : null;
    const token = storage?.getItem('ftedn_token');
    if (token) headers['Authorization'] = `Bearer ${token}`;
    const appCheckToken = await getAppCheckToken();
    if (appCheckToken) headers['X-Firebase-AppCheck'] = appCheckToken;
    return headers;
  }

  private async getBase(): Promise<string> {
    const config = await getConfig();
    return config.apiBase;
  }

  private async parseError(res: Response, method: string, path: string): Promise<Error> {
    try {
      const body = await res.json();
      const message =
        body?.error?.message ||
        body?.message ||
        body?.reason ||
        body?.error ||
        `${method} ${path} failed: ${res.status}`;
      const code = typeof body?.error?.code === 'string' ? body.error.code : undefined;
      return new ApiClientError(String(message), code);
    } catch {
      return new Error(`${method} ${path} failed: ${res.status}`);
    }
  }

  async get(path: string) {
    const base = await this.getBase();
    const res = await fetch(`${base}${path}`, { headers: await this.getHeaders() });
    if (!res.ok) throw await this.parseError(res, 'GET', path);
    return res.json();
  }

  async post(path: string, body?: unknown) {
    const base = await this.getBase();
    const res = await fetch(`${base}${path}`, {
      method: 'POST', headers: await this.getHeaders(), body: JSON.stringify(body),
    });
    if (!res.ok) throw await this.parseError(res, 'POST', path);
    return res.json();
  }

  async patch(path: string, body?: unknown) {
    const base = await this.getBase();
    const res = await fetch(`${base}${path}`, {
      method: 'PATCH', headers: await this.getHeaders(), body: JSON.stringify(body),
    });
    if (!res.ok) throw await this.parseError(res, 'PATCH', path);
    return res.json();
  }

  async delete(path: string, body?: unknown) {
    const base = await this.getBase();
    const requestInit: RequestInit = {
      method: 'DELETE',
      headers: await this.getHeaders(),
    };
    if (body !== undefined) {
      requestInit.body = JSON.stringify(body);
    }
    const res = await fetch(`${base}${path}`, requestInit);
    if (!res.ok) throw await this.parseError(res, 'DELETE', path);
    return res.json();
  }
}

export const apiClient = new ApiClient();

// React hook — returns bound API methods with auth headers baked in.
// Use this in components instead of raw fetch().
export function useApi() {
  return useMemo(() => ({
    get: (path: string) => apiClient.get(path),
    post: (path: string, body?: unknown) => apiClient.post(path, body),
    patch: (path: string, body?: unknown) => apiClient.patch(path, body),
    del: (path: string, body?: unknown) => apiClient.delete(path, body),
  }), []);
}
